import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { postJSON } from '@/lib/api'

type TicketType = { id: number; name: string }

export function RemoveEntryMenu({
  entry,
  onRemove,
  onOffer,
  offerPending = false,
}: {
  entry: { id: number; name: string }
  onRemove: (id: number) => void
  onOffer?: () => void
  offerPending?: boolean
}) {
  const [open, setOpen] = React.useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Actions for ${entry.name}`}
          >
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {onOffer && (
            <DropdownMenuItem disabled={offerPending} onSelect={onOffer}>
              Send offer now
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="text-destructive" onSelect={() => setOpen(true)}>
            Remove from waitlist
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {entry.name}?</DialogTitle>
          <DialogDescription>
            This entry will leave the active queue and remain available under the Removed filter. No
            email will be sent.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Keep on waitlist
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              onRemove(entry.id)
              setOpen(false)
            }}
          >
            Remove from waitlist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function ManualAddDialog({
  eventId,
  ticketTypes,
  onAdded,
}: {
  eventId: number
  ticketTypes: TicketType[]
  onAdded: () => void
}) {
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [ticketTypeId, setTicketTypeId] = React.useState(
    ticketTypes[0] ? String(ticketTypes[0].id) : '',
  )
  const [partySize, setPartySize] = React.useState('1')
  const add = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/events/${eventId}/waitlist_entries`, {
        waitlist_entry: {
          name,
          email,
          event_ticket_type_id: Number(ticketTypeId),
          party_size: Number(partySize),
        },
      }),
    onSuccess: () => {
      setOpen(false)
      onAdded()
    },
  })
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Add to waitlist</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to waitlist</DialogTitle>
          <DialogDescription>
            Add a phone joiner to the end of a Ticket Type queue.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            aria-label="Name"
            placeholder="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            aria-label="Email"
            placeholder="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Select value={ticketTypeId} onValueChange={setTicketTypeId}>
            <SelectTrigger aria-label="Ticket type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ticketTypes.map((ticket) => (
                <SelectItem key={ticket.id} value={String(ticket.id)}>
                  {ticket.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            aria-label="Party size"
            type="number"
            min="1"
            value={partySize}
            onChange={(event) => setPartySize(event.target.value)}
          />
          {add.isError && (
            <p className="text-body-sm text-destructive">
              Could not add this person to the waitlist.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => add.mutate()} disabled={add.isPending}>
            Add to waitlist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
